import { PartialBy } from './types';
import { MainRoutes } from './paths';
import { HttpStatus } from '@nestjs/common';
import { capitalize, create, join, split } from 'lodash';
export type APIErrorInfo = {
  /**
   * the error id. This id will be prefixed with the module name such as auth/customer/etc
   */
  errorId: string;
  /**
   * the message to be sent to the user
   */
  message: string;
  /**
   * HTTP Status
   */
  statusCode: keyof typeof HttpStatus;
};

const createError = (info: PartialBy<APIErrorInfo, 'message'>): APIErrorInfo => {
  const getMSG = () => {
    const message = info.message;
    if (message) return message;
    const msgSplitted = split(info.errorId, '_');
    const generatedMessage = join(msgSplitted, ' ');
    return capitalize(generatedMessage);
  };
  const { errorId, message = getMSG(), statusCode } = info;

  return { errorId, message, statusCode };
};

export const API_ERRORS = {
  [MainRoutes.auth]: {
    user_unable_to_sign_in_after_signup: createError({
      errorId: 'auth_user_unable_to_sign_in_after_signup',
      statusCode: 'UNAUTHORIZED',
      message: 'After the user has signed up, we are unable to sing in',
    }),
    user_is_new: createError({
      errorId: 'auth_user_is_new',
      statusCode: 'UNAUTHORIZED',
      message: 'The current user is new and has not completed registration yet',
    }),
    user_type_not_allowed: createError({
      errorId: 'user_type_not_allowed',
      statusCode: 'UNAUTHORIZED',
      message: 'The user type is not allowed to preform this action',
    }),
    unauthorized: createError({
      errorId: 'unauthorized',
      statusCode: 'UNAUTHORIZED',
    }),
    otp_is_expired: createError({
      errorId: 'otp_is_expired',
      statusCode: 'BAD_REQUEST',
    }),
    user_already_have_account: createError({
      errorId: 'user_already_have_account',
      statusCode: 'UNAUTHORIZED',
    }),
    phone_number_is_duplicated: createError({
      errorId: 'phone_number_is_duplicated',
      statusCode: 'BAD_REQUEST',
    }),
    phone_number_does_not_match: createError({
      errorId: 'phone_number_does_not_match',
      statusCode: 'BAD_REQUEST',
    }),
    already_verified: createError({
      errorId: 'already_verified',
      statusCode: 'NOT_ACCEPTABLE',
    }),
    error_while_sending_sms: createError({
      errorId: 'error_while_sending_sms',
      statusCode: 'BAD_REQUEST',
    }),
    too_many_tries: createError({
      errorId: 'to_many_tries',
      statusCode: 'TOO_MANY_REQUESTS',
    }),
    otp_rate_limited: createError({
      errorId: 'auth_otp_rate_limited',
      statusCode: 'TOO_MANY_REQUESTS',
      message: 'Too many verification codes requested. Please wait before trying again.',
    }),
    otp_verification_expired: createError({
      errorId: 'auth_otp_verification_expired',
      statusCode: 'UNAUTHORIZED',
      message: 'Phone verification has expired. Verify the number again to continue signing up.',
    }),
    wrong_otp: createError({
      errorId: 'wrong_otp',
      statusCode: 'NOT_FOUND',
    }),
    otp_not_found: createError({
      errorId: 'otp_not_found',
      statusCode: 'NOT_FOUND',
    }),
    refresh_missing: createError({
      errorId: 'auth_refresh_missing',
      statusCode: 'UNAUTHORIZED',
      message: 'A refresh token is required',
    }),
    refresh_invalid: createError({
      errorId: 'auth_refresh_invalid',
      statusCode: 'UNAUTHORIZED',
      message: 'The refresh token is not valid',
    }),
    refresh_session_not_found: createError({
      errorId: 'auth_refresh_session_not_found',
      statusCode: 'UNAUTHORIZED',
      message: 'The refresh session was not found',
    }),
    refresh_session_revoked: createError({
      errorId: 'auth_refresh_session_revoked',
      statusCode: 'UNAUTHORIZED',
      message: 'This refresh session has been revoked',
    }),
    refresh_reuse_detected: createError({
      errorId: 'auth_refresh_reuse_detected',
      statusCode: 'UNAUTHORIZED',
      message:
        'Refresh token reuse detected. This session and every device using it has been revoked.',
    }),
    refresh_session_expired: createError({
      errorId: 'auth_refresh_session_expired',
      statusCode: 'UNAUTHORIZED',
      message: 'The refresh session has expired. Please sign in again.',
    }),
    user_not_found: createError({
      errorId: 'auth_user_not_found',
      statusCode: 'UNAUTHORIZED',
      message: 'The user linked to this session no longer exists',
    }),
    super_admin_only: createError({
      errorId: 'auth_super_admin_only',
      statusCode: 'FORBIDDEN',
      message: 'Only super admins may perform this action.',
    }),
    user_blocked: createError({
      errorId: 'auth_user_blocked',
      statusCode: 'FORBIDDEN',
      message: 'Your account has been blocked. Please contact support.',
    }),
  },
  [MainRoutes.clients]: {
    org_not_found: createError({
      errorId: 'clients_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The org was not found in the current user relation',
    }),
  },

  [MainRoutes.lead]: {
    missing_identifier: createError({
      errorId: 'lead_missing_identifier',
      statusCode: 'BAD_REQUEST',
      message: 'A lead needs at least one of: company name, person phone, or person email.',
    }),
    missing_company_name: createError({
      errorId: 'lead_missing_company_name',
      statusCode: 'BAD_REQUEST',
      message: 'A company name is required for admin-created leads.',
    }),
    not_found: createError({
      errorId: 'lead_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The lead was not found.',
    }),
    invalid_person_index: createError({
      errorId: 'lead_invalid_person_index',
      statusCode: 'BAD_REQUEST',
      message: 'Invalid people[] index for this lead.',
    }),
    invalid_action_index: createError({
      errorId: 'lead_invalid_action_index',
      statusCode: 'BAD_REQUEST',
      message: 'Invalid actions[] index for this lead.',
    }),
    lost_reason_required: createError({
      errorId: 'lead_lost_reason_required',
      statusCode: 'BAD_REQUEST',
      message:
        'A lost reason is required when moving a lead to a terminal status (lost / unqualified / dead).',
    }),
    convert_invalid_status: createError({
      errorId: 'lead_convert_invalid_status',
      statusCode: 'BAD_REQUEST',
      message:
        'A lead can only be converted from a non-terminal status (new / contacted / qualified).',
    }),
    public_rate_limited: createError({
      errorId: 'lead_public_rate_limited',
      statusCode: 'TOO_MANY_REQUESTS',
      message: 'Too many submissions from this IP. Please try again later.',
    }),
    // Best-effort hook (reported, never thrown) — see the `logging` skill.
    signup_stamp_failed: createError({
      errorId: 'lead_signup_stamp_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'The CRM lead for a self-serve signup could not be recorded',
    }),
    assignee_not_found: createError({
      errorId: 'lead_assignee_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The AdminUser assignee was not found.',
    }),
  },
  [MainRoutes.ticket]: {
    not_found: createError({
      errorId: 'ticket_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The ticket was not found.',
    }),
    assignee_not_found: createError({
      errorId: 'ticket_assignee_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The AdminUser assignee was not found.',
    }),
  },
  [MainRoutes.lender]: {
    slug_conflict: createError({
      errorId: 'lender_slug_conflict',
      statusCode: 'BAD_REQUEST',
      message: 'A lender with this slug already exists',
    }),
    forbidden: createError({
      errorId: 'lender_forbidden',
      statusCode: 'FORBIDDEN',
      message: 'Only super admins can manage the lender directory',
    }),
    not_found: createError({
      errorId: 'lender_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The lender was not found',
    }),
  },

  [MainRoutes.referral]: {
    forbidden: createError({
      errorId: 'referral_forbidden',
      statusCode: 'FORBIDDEN',
      message: 'Only super admins can manage the referral program',
    }),
    partner_not_found: createError({
      errorId: 'referral_partner_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The referral partner was not found',
    }),
    link_not_found: createError({
      errorId: 'referral_link_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The referral link was not found',
    }),
    code_conflict: createError({
      errorId: 'referral_code_conflict',
      statusCode: 'BAD_REQUEST',
      message: 'A referral link with this code already exists',
    }),
    link_business_not_allowed: createError({
      errorId: 'referral_link_business_not_allowed',
      statusCode: 'BAD_REQUEST',
      message: 'This partner is not set up to market that business',
    }),
    code_generation_failed: createError({
      errorId: 'referral_code_generation_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Could not generate a unique referral code',
    }),
    reward_not_found: createError({
      errorId: 'referral_reward_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The referral reward was not found',
    }),
    reward_not_pending: createError({
      errorId: 'referral_reward_not_pending',
      statusCode: 'BAD_REQUEST',
      message: 'Only pending rewards can be settled or voided',
    }),
    credits_owner_missing: createError({
      errorId: 'referral_credits_owner_missing',
      statusCode: 'BAD_REQUEST',
      message: 'An order_credits reward needs a partner with a platform account (owner)',
    }),
    tracking_rate_limited: createError({
      errorId: 'referral_tracking_rate_limited',
      statusCode: 'TOO_MANY_REQUESTS',
      message: 'Too many tracking requests. Please slow down.',
    }),
    // Best-effort hooks (reported, never thrown) — see the `logging` skill.
    attribution_failed: createError({
      errorId: 'referral_attribution_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Referral attribution could not be written',
    }),
    reward_failed: createError({
      errorId: 'referral_reward_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Referral reward could not be evaluated for a settled billing',
    }),
    tracking_failed: createError({
      errorId: 'referral_tracking_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Referral tracking hit could not be recorded',
    }),
  },

  [MainRoutes.order]: {
    //otp_is_expired: createError('otp_is_expired'),
    destination_missing: createError({
      errorId: 'order_destination_missing',
      statusCode: 'BAD_REQUEST',
      message: 'Missing Pickup or Drop off location',
    }),

    cannot_cancel_order: createError({
      errorId: 'order_cannot_cancel_order',
      statusCode: 'BAD_REQUEST',
      message:
        'The order cannot be canceled in its current status. Only undelivered orders can be canceled.',
    }),
    missing_cancellation_info: createError({
      errorId: 'order_missing_cancellation_info',
      statusCode: 'BAD_REQUEST',
      message: 'The cancellation info is missing to cancel the order',
    }),
    /**
     * @deprecated Cross-city destination edits are supported now — see
     * `locationChangePricing` on `OrderUpdateAPI`. The remaining refusals
     * are the three specific ones below, each of which names a condition
     * the caller can actually act on.
     */
    destinations_updated_not_in_same_city: createError({
      errorId: 'order_destinations_updated_not_in_same_city',
      statusCode: 'BAD_REQUEST',
      message:
        'The destinations updated are not in the same city. When updating destinations, you must keep them in the same city, otherwise, you need to create a new order.',
    }),
    cross_city_needs_pricing_choice: createError({
      errorId: 'order_cross_city_needs_pricing_choice',
      statusCode: 'BAD_REQUEST',
      message:
        'This edit moves a stop to a different city, which changes the lane. Send `locationChangePricing` (`resolve_route` to re-quote the new lane, `keep_previous` to hold the current price, or `custom` with `locationChangePrice`), or send an explicit price with `beneficiaryAmount` + `contractId`.',
    }),
    cross_city_custom_price_missing: createError({
      errorId: 'order_cross_city_custom_price_missing',
      statusCode: 'BAD_REQUEST',
      message:
        "`locationChangePricing: 'custom'` needs `locationChangePrice` — the load price to apply to the new lane.",
    }),
    cross_city_blocked_in_transit: createError({
      errorId: 'order_cross_city_blocked_in_transit',
      statusCode: 'BAD_REQUEST',
      message:
        'The order has already left the pickup, so the city cannot be changed. Create a new order for the new destination.',
    }),
    cross_city_blocked_pickup_locked: createError({
      errorId: 'order_cross_city_blocked_pickup_locked',
      statusCode: 'BAD_REQUEST',
      message:
        'The truck has already left the pickup, so the pickup city cannot be changed. Create a new order, or change only the dropoff city if that is allowed.',
    }),
    cross_city_blocked_dropoff_locked: createError({
      errorId: 'order_cross_city_blocked_dropoff_locked',
      statusCode: 'BAD_REQUEST',
      message:
        'The truck has already left the pickup, so the dropoff city cannot be changed under the current policy. Create a new order for the new destination.',
    }),
    cross_city_blocked_stop_count: createError({
      errorId: 'order_cross_city_blocked_stop_count',
      statusCode: 'BAD_REQUEST',
      message:
        'Adding or removing a stop is not supported on an update. Keep the same number of stops, or create a new order.',
    }),
    missing_problem_image: createError({
      errorId: 'order_missing_problem_image',
      statusCode: 'BAD_REQUEST',
      message: 'You must include images that proof the problem',
    }),
    cannot_assign_driver: createError({
      errorId: 'order_cannot_assign_driver',
      statusCode: 'BAD_REQUEST',
      message: 'The driver cannot be assigned to the order because he is not active or available',
    }),
    cannot_assign_vehicle: createError({
      errorId: 'order_cannot_assign_vehicle',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle cannot be assigned to the order because  it is not active or available',
    }),

    fee_calc_type_not_found: createError({
      errorId: 'order_fee_calc_type_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The fee type is not found in the price calculation type',
    }),
    not_found: createError({
      errorId: 'order_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The order was not found',
    }),
    public_rate_limited: createError({
      errorId: 'order_public_rate_limited',
      statusCode: 'TOO_MANY_REQUESTS',
      message: 'Too many tracking lookups from this IP. Please try again later.',
    }),
    contract_not_found: createError({
      errorId: 'order_contract_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The specified contract leg was not found on this order',
    }),
    unauthorized_contract_edit: createError({
      errorId: 'order_unauthorized_contract_edit',
      statusCode: 'FORBIDDEN',
      message: 'Only a party (buyer or seller) of this contract leg can edit its price.',
    }),
    contract_leg_missing: createError({
      errorId: 'order_contract_leg_missing',
      statusCode: 'BAD_REQUEST',
      message:
        'No contract leg exists for the requested payment perspective on this order. Walk-in orders cannot be paid until a leg is seeded.',
    }),
    only_originator_can_promote_public: createError({
      errorId: 'order_only_originator_can_promote_public',
      statusCode: 'FORBIDDEN',
      message:
        'Only the order originator (Order.shipper) can broadcast an offer to the public load board',
    }),
    not_a_tenant: createError({
      errorId: 'order_not_a_tenant',
      statusCode: 'FORBIDDEN',
      message:
        'You do not have access to this order. If you have an incoming offer for it, open the offer first.',
    }),
  },

  offer: {
    not_found: createError({
      errorId: 'offer_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The offer was not found',
    }),
    target_not_found: createError({
      errorId: 'offer_target_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The specified target row was not found on this offer',
    }),
    target_not_open: createError({
      errorId: 'offer_target_not_open',
      statusCode: 'BAD_REQUEST',
      message:
        'This offer target is not open. It may have been accepted, declined, withdrawn, or expired.',
    }),
    invalid_visibility_for_creator: createError({
      errorId: 'offer_invalid_visibility_for_creator',
      statusCode: 'FORBIDDEN',
      message:
        'The chosen visibility is not allowed for this creator. Only the order originator may publish to the public load board.',
    }),
    proposed_buyer_not_a_contract_holder: createError({
      errorId: 'offer_proposed_buyer_not_a_contract_holder',
      statusCode: 'FORBIDDEN',
      message:
        'The proposedBuyer must have a buy-side accepted contract on this order before they can broadcast it downstream.',
    }),
    not_target: createError({
      errorId: 'offer_not_target',
      statusCode: 'FORBIDDEN',
      message: 'You are not a target on this offer.',
    }),
    not_owner: createError({
      errorId: 'offer_not_owner',
      statusCode: 'FORBIDDEN',
      message: 'Only the proposedBuyer can perform this action on this offer.',
    }),
  },

  [MainRoutes.user]: {
    //otp_is_expired: createError('otp_is_expired'),
    target_not_found: createError({
      errorId: 'user_target_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The target user type is NOT found',
    }),
    block_meta_required: createError({
      errorId: 'user_block_meta_required',
      statusCode: 'BAD_REQUEST',
      message: 'A block reason is required when blocking a user.',
    }),
  },
  wallet: {
    invalid_info: createError({
      errorId: 'wallet_invalid_info',
      statusCode: 'BAD_REQUEST',
      message:
        'The wallet info is invalid. You must provide either a business, individual customer, carrier company driver, or freelancer driver',
    }),
    currency_mismatch: createError({
      errorId: 'wallet_currency_mismatch',
      statusCode: 'BAD_REQUEST',
      message: 'The wallet currency does not match the required currency',
    }),
    not_found: createError({
      errorId: 'wallet_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The wallet was not found',
    }),
  },
  [MainRoutes.member]: {
    //otp_is_expired: createError('otp_is_expired'),
    user_already_in_org: createError({
      errorId: 'member_user_already_in_org',
      statusCode: 'BAD_REQUEST',
      message: 'The user has been already added to the org',
    }),
    user_not_found_in_org: createError({
      errorId: 'member_user_not_found_in_org',
      statusCode: 'BAD_REQUEST',
      message: 'The user does not belong to provided the org id',
    }),
    type_not_found: createError({
      errorId: 'member_type_not_found',
      statusCode: 'INTERNAL_SERVER_ERROR',
    }),
    no_owner_was_assigned: createError({
      errorId: 'member_no_owner_was_assigned',
      statusCode: 'BAD_REQUEST',
      message: 'No Carrier / Driver / or Owner was assigned to the vehicle',
    }),
    missing_driver_info: createError({
      errorId: 'member_missing_driver_info',
      statusCode: 'BAD_REQUEST',
      message: 'Add all driver info when create a new driver member (name, id, etc)',
    }),
    user_must_belong_to_org: createError({
      errorId: 'member_user_must_belong_to_org',
      statusCode: 'BAD_REQUEST',
      message:
        'The current user is individual and does not belong to any organization, therefore, there are not members to return.',
    }),
    user_cannot_be_deleted_active_order: createError({
      errorId: 'member_user_cannot_be_deleted_active_order',
      statusCode: 'BAD_REQUEST',
      message: 'The user driver cannot because the driver has an active order.',
    }),
    org_not_found: createError({
      errorId: 'member_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The org id is not found in the user related orgs',
    }),
    phone_locked_after_sign_in: createError({
      errorId: 'member_phone_locked_after_sign_in',
      statusCode: 'BAD_REQUEST',
      message: 'The phone number cannot be changed because this user has already signed in with it',
    }),
    driver_phone_taken: createError({
      errorId: 'member_driver_phone_taken',
      statusCode: 'BAD_REQUEST',
      message: 'Another driver on this company already uses this phone number',
    }),
    phone_taken: createError({
      errorId: 'member_phone_taken',
      statusCode: 'BAD_REQUEST',
      message: 'This phone number already belongs to another account',
    }),
  },
  [MainRoutes.vehicle]: {
    //otp_is_expired: createError('otp_is_expired'),
    carrierId_not_found_in_payload: createError({
      errorId: 'vehicle_carrierId_not_found_in_payload',
      statusCode: 'BAD_REQUEST',
    }),
    owner_legal_name_is_required: createError({
      errorId: 'vehicle_owner_legal_name_is_required',
      statusCode: 'BAD_REQUEST',
      message: 'The owner legal name is required when the owner is different from the carrier',
    }),
    plate_already_on_fleet: createError({
      errorId: 'vehicle_plate_already_on_fleet',
      statusCode: 'BAD_REQUEST',
      message: 'A vehicle with this plate is already on this fleet — edit that vehicle instead',
    }),
    on_another_driver: createError({
      errorId: 'vehicle_on_another_driver',
      statusCode: 'BAD_REQUEST',
      message:
        'This vehicle belongs to another saved driver (by its current driver or by the operator id on its registration) — move it only when explicitly asked',
    }),
  },
  [MainRoutes.location]: {
    invalid: createError({ errorId: 'location_invalid', statusCode: 'BAD_REQUEST' }),
    invalid_place_details: createError({
      errorId: 'location_invalid_place_details',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while getting the place details',
    }),
    search_not_found: createError({
      errorId: 'location_search_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'No result found for your search',
    }),
    /**
     * this happens when for some reason the backend cloud not find the city or country name within google map response
     */
    unable_to_find_required_param: createError({
      errorId: 'location_unable_to_find_required_param',
      statusCode: 'INTERNAL_SERVER_ERROR',
    }),
  },
  [MainRoutes.driver]: {
    orgId_not_found_in_payload: createError({
      errorId: 'driver_orgId_not_found_in_payload',
      statusCode: 'BAD_REQUEST',
    }),
  },
  savedLocation: {
    org_not_found: createError({
      errorId: 'savedLocation_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The org was not found in the current user relation',
    }),
  },
  validation: {
    boolean_is_not_valid: createError({
      errorId: 'validation_boolean_is_not_valid',
      statusCode: 'BAD_REQUEST',
      message: 'The boolean value is not valid',
    }),
    data_is_not_valid: createError({
      errorId: 'data_is_not_valid',
      statusCode: 'BAD_REQUEST',
    }),
  },
  other: {
    unknown_error: createError({
      errorId: 'unknown_error',
      statusCode: 'BAD_REQUEST',
    }),
    analytics_capture_failed: createError({
      errorId: 'other_analytics_capture_failed',
      statusCode: 'BAD_REQUEST',
      message: 'Product analytics write failed (swallowed — the originating action continues)',
    }),
    analytics_query_failed: createError({
      errorId: 'other_analytics_query_failed',
      statusCode: 'BAD_GATEWAY',
      message: 'Product analytics query (PostHog HogQL) failed',
    }),
    agent_configuration: createError({
      errorId: 'other_agent_configuration',
      statusCode: 'BAD_REQUEST',
      message: 'Hero AI is misconfigured (missing provider key or account outage)',
    }),
  },
  image: {
    no_value_provided: createError({
      errorId: 'image_no_value_provided',
      statusCode: 'BAD_REQUEST',
      message: 'No value upload or link & id was provided',
    }),
    not_valid: createError({
      errorId: 'image_not_valid',
      statusCode: 'BAD_REQUEST',
      message: 'Not a valid image to upload',
    }),
    failed_to_upload: createError({
      errorId: 'image_failed_to_upload',
      statusCode: 'BAD_REQUEST',
    }),
    failed_to_delete: createError({
      errorId: 'image_failed_to_delete',
      statusCode: 'BAD_REQUEST',
    }),
    failed_to_retrieve_image: createError({
      errorId: 'image_failed_to_retrieve_image',
      statusCode: 'BAD_REQUEST',
    }),
    failed_to_update_metadata: createError({
      errorId: 'image_failed_to_update_metadata',
      statusCode: 'BAD_REQUEST',
    }),
  },
  /**
   * Media we store rather than transform — voice clips today, call
   * recordings later. Separate from `image` because the storage
   * backend is different (private GCS bucket, signed reads) and so are
   * the failure modes. See ADR-006.
   */
  media: {
    storage_not_configured: createError({
      errorId: 'media_storage_not_configured',
      statusCode: 'BAD_REQUEST',
      message: 'Media storage bucket is not configured (GCS_VOICE_BUCKET)',
    }),
    failed_to_store: createError({
      errorId: 'media_failed_to_store',
      statusCode: 'BAD_REQUEST',
    }),
    failed_to_sign: createError({
      errorId: 'media_failed_to_sign',
      statusCode: 'BAD_REQUEST',
      message: 'Could not mint a signed read URL for stored media',
    }),
    not_found: createError({
      errorId: 'media_not_found',
      statusCode: 'NOT_FOUND',
      message: 'No stored media matches that reference',
    }),
    access_denied: createError({
      errorId: 'media_access_denied',
      statusCode: 'FORBIDDEN',
      message: 'You do not have access to the conversation that owns this media',
    }),
  },
  /** Speech-to-text and (later) text-to-speech. See `docs/ai/voice/`. */
  voice: {
    stt_failed: createError({
      errorId: 'voice_stt_failed',
      statusCode: 'BAD_REQUEST',
      message: 'Speech-to-text failed for an inbound voice clip',
    }),
  },
  /** Reply language / script. See `docs/ai/multi-language.md`. */
  agent_language: {
    picker_failed: createError({
      errorId: 'agent_language_picker_failed',
      statusCode: 'BAD_REQUEST',
      message: 'The language picker could not be sent or applied (turn continued without it)',
    }),
    reply_mismatch: createError({
      errorId: 'agent_language_reply_mismatch',
      statusCode: 'BAD_REQUEST',
      message: 'An agent reply was written in a different script than the thread language',
    }),
  },
  /** Inbound photos / PDFs. See `docs/ai/multimodal.md`. */
  agent_media: {
    file_unreadable: createError({
      errorId: 'agent_media_file_unreadable',
      statusCode: 'BAD_REQUEST',
      message:
        'A file the user sent could not be read by vision (the turn told the model and continued)',
    }),
    sticker_caption_failed: createError({
      errorId: 'agent_media_sticker_caption_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A sticker could not be captioned (the turn replayed it as a bare sticker)',
    }),
  },
  /** Staff-facing voice-note summaries. See `docs/ai/voice/summaries.md`. */
  agent_voice: {
    summary_failed: createError({
      errorId: 'agent_voice_summary_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A voice-note summary could not be written (the transcript is unaffected)',
    }),
    confirm_reply_failed: createError({
      errorId: 'agent_voice_confirm_reply_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'The model could not word a confirm result (the code text was sent instead)',
    }),
    must_include_missed: createError({
      errorId: 'agent_voice_must_include_missed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message:
        'The reply dropped a value a tool required (the value was appended); a voice v2 rollout signal',
    }),
  },
  /** Reply claims vs this turn's writes. See `docs/ai/conversation-brief.md` § Reply claims. */
  agent_reply: {
    unbacked_claim: createError({
      errorId: 'agent_reply_unbacked_claim',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message:
        'A reply said something was done that no write this turn and nothing on file backs (it was rewritten once)',
    }),
  },
  /** Agent copy from `@hero/lang` `m-agent.json`. See `docs/ai/multi-language.md` § Agent copy. */
  agent_copy: {
    missing_key: createError({
      errorId: 'agent_copy_missing_key',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'An agent copy key is missing in one language (English was sent instead)',
    }),
  },
  /** Consistency layer (ADR 033): name judge, alias learning, ask guard. */
  agent_consistency: {
    name_judge_failed: createError({
      errorId: 'agent_consistency_name_judge_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'The name judge could not answer (every candidate was treated as unsure)',
    }),
    alias_learn_failed: createError({
      errorId: 'agent_consistency_alias_learn_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A confirmed alias could not be stored (the next match goes through the judge again)',
    }),
    redundant_ask: createError({
      errorId: 'agent_consistency_redundant_ask',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A reply asked for a fact already known (logged in shadow, regenerated once when on)',
    }),
    memory_write_failed: createError({
      errorId: 'agent_consistency_memory_write_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Conversation memory could not be saved (the turn continued without it)',
    }),
    typed_card: createError({
      errorId: 'agent_consistency_typed_card',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'The model typed a confirmation card with nothing staged (dropped, one corrective pass)',
    }),
    turn_owner_lost: createError({
      errorId: 'agent_consistency_turn_owner_lost',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A turn died holding the conversation lock (the sweeper answered its unread messages)',
    }),
  },
  /** On-demand staff translation of a message. See `docs/ai/staff-text.md`. */
  agent_translation: {
    failed: createError({
      errorId: 'agent_translation_failed',
      statusCode: 'BAD_GATEWAY',
      message: 'The message could not be translated right now',
    }),
  },
  agent_message: {
    not_found: createError({
      errorId: 'agent_message_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The message was not found on this conversation',
    }),
  },
  db: {
    // events
    event_on_create: createError({
      errorId: 'db_event_on_create',
      statusCode: 'BAD_REQUEST',
    }),
    event_on_update: createError({
      errorId: 'db_event_on_update',
      statusCode: 'BAD_REQUEST',
    }),
    event_on_change: createError({
      errorId: 'db_event_on_change',
      statusCode: 'BAD_REQUEST',
    }),
    event_on_delete: createError({
      errorId: 'db_event_on_delete',
      statusCode: 'BAD_REQUEST',
    }),
    // crud
    must_have_update_info: createError({
      errorId: 'db_must_have_update_info',
      statusCode: 'BAD_REQUEST',
    }),

    transaction_error: createError({ errorId: 'db_transaction_error', statusCode: 'BAD_REQUEST' }),

    validation_error: createError({
      errorId: 'db_validation_error',
      statusCode: 'BAD_REQUEST',
    }),
    invalid_id: createError({
      errorId: 'db_invalid_id',
      statusCode: 'BAD_REQUEST',
      message: 'Invalid Object Id',
    }),
    update_error: createError({
      errorId: 'db_update_error',
      statusCode: 'BAD_REQUEST',
    }),
    doc_not_found: createError({
      errorId: 'db_doc_not_found',
      statusCode: 'BAD_REQUEST',
    }),
    other_error: createError({
      errorId: 'db_other_error',
      statusCode: 'BAD_REQUEST',
    }),
    create_error: createError({
      errorId: 'db_create_error',
      statusCode: 'BAD_REQUEST',
    }),
  },
  monday: {
    item_not_found: createError({
      errorId: 'monday_item_not_found',
      statusCode: 'BAD_REQUEST',
    }),
    order_id_is_missing: createError({
      errorId: 'monday_order_id_is_missing',
      statusCode: 'BAD_REQUEST',
    }),
  },
  document: {
    error_generating_doc: createError({
      errorId: 'document_error_generating_doc',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while generating a new document',
    }),
  },
  [MainRoutes.region]: {
    location_info_not_found: createError({
      errorId: 'region_location_info_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'Not able to get the location details based on the type of region or wrong lat/lng',
    }),
    import_file_missing: createError({
      errorId: 'region_import_file_missing',
      statusCode: 'BAD_REQUEST',
      message: 'No .xlsx file was attached to the import request',
    }),
  },
  [MainRoutes.apps]: {
    not_configured: createError({
      errorId: 'apps_not_configured',
      statusCode: 'SERVICE_UNAVAILABLE',
      message:
        'This app is not configured on the Hero platform yet (missing provider credentials). Contact support.',
    }),
    not_connectable: createError({
      errorId: 'apps_not_connectable',
      statusCode: 'BAD_REQUEST',
      message: 'This app has no connector yet. It is listed so we can gauge interest.',
    }),
    not_installed: createError({
      errorId: 'apps_not_installed',
      statusCode: 'BAD_REQUEST',
      message:
        'Your organization has not installed this app yet. Install it from Apps & Integrations first.',
    }),
    already_installed: createError({
      errorId: 'apps_already_installed',
      statusCode: 'BAD_REQUEST',
      message: 'Your organization has already installed this app.',
    }),
    invalid_credentials: createError({
      errorId: 'apps_invalid_credentials',
      statusCode: 'BAD_REQUEST',
      message:
        'The provider rejected these credentials. Check the client id on your Bayan portal profile page and that your NAQL license is valid.',
    }),
    missing_cr_number: createError({
      errorId: 'apps_missing_cr_number',
      statusCode: 'BAD_REQUEST',
      message:
        'A unified establishment number (الرقم الموحد / هوية صاحب العمل) starting with 70 is required before installing.',
    }),
    invalid_cr_number: createError({
      errorId: 'apps_invalid_cr_number',
      statusCode: 'BAD_REQUEST',
      message:
        'That is not a unified establishment number. It must start with 70 (الرقم الموحد). Old commercial registrations that start with 1 are not accepted.',
    }),
    // The identity an owner-operator files under alongside هوية صاحب العمل.
    // Bayan still requires the person's national id / iqama and the issue
    // number printed on the card, and neither can be derived from the other.
    missing_driver_identity: createError({
      errorId: 'apps_missing_driver_identity',
      statusCode: 'BAD_REQUEST',
      message:
        'You need your national id / iqama number and its issue number (رقم إصدار الهوية) on file before issuing. Bayan names you as the carrier on every waybill.',
    }),
    // Distinct from `not_configured`, which means Hero holds no ELM
    // credentials at all. This one means the connector is deliberately
    // switched off for this caller during rollout — same HTTP status, very
    // different support answer, so it must not be folded into that one.
    bayan_disabled: createError({
      errorId: 'apps_bayan_disabled',
      statusCode: 'SERVICE_UNAVAILABLE',
      message:
        'Government e-waybill filing (وثيقة النقل) is temporarily unavailable and will be back soon.',
    }),
    provider_unavailable: createError({
      errorId: 'apps_provider_unavailable',
      statusCode: 'BAD_GATEWAY',
      message: 'The provider is not responding. The request was recorded and will be retried.',
    }),
    // Distinct from `provider_unavailable` because nothing was recorded and
    // nothing will be retried — an install only exists once the provider has
    // confirmed the credentials, so an outage means "try again", not "queued".
    verification_unavailable: createError({
      errorId: 'apps_verification_unavailable',
      statusCode: 'BAD_GATEWAY',
      message:
        'The provider is not responding, so these credentials could not be verified. Nothing was saved — try connecting again in a few minutes.',
    }),
    provider_rejected: createError({
      errorId: 'apps_provider_rejected',
      statusCode: 'BAD_REQUEST',
      message: 'The provider rejected the request. See the error detail for what to fix.',
    }),
  },
  [MainRoutes.bayan]: {
    waybill_not_ready: createError({
      errorId: 'bayan_waybill_not_ready',
      statusCode: 'BAD_REQUEST',
      message:
        'This order is missing data the government waybill requires. Resolve the listed blockers and try again.',
    }),
    waybill_cancel_failed: createError({
      errorId: 'bayan_waybill_cancel_failed',
      statusCode: 'BAD_REQUEST',
      message:
        'Bayan did not cancel the current waybill, so nothing was changed. The original document is still valid.',
    }),
    waybill_not_issued: createError({
      errorId: 'bayan_waybill_not_issued',
      statusCode: 'BAD_REQUEST',
      message: 'No government waybill has been issued for this order yet.',
    }),
    order_not_found: createError({
      errorId: 'bayan_order_not_found',
      statusCode: 'NOT_FOUND',
      message: 'The order was not found, or your organization is not a party to it.',
    }),
    waybill_issue_failed: createError({
      errorId: 'bayan_waybill_issue_failed',
      statusCode: 'BAD_REQUEST',
      message: 'The government waybill could not be issued.',
    }),
    waybill_pdf_failed: createError({
      errorId: 'bayan_waybill_pdf_failed',
      statusCode: 'BAD_GATEWAY',
      message: 'The government waybill was issued but the PDF could not be archived.',
    }),
    waybill_deliver_failed: createError({
      errorId: 'bayan_waybill_deliver_failed',
      statusCode: 'BAD_GATEWAY',
      message: 'The government waybill PDF could not be delivered.',
    }),
    waybill_retry_gave_up: createError({
      errorId: 'bayan_waybill_retry_gave_up',
      statusCode: 'BAD_GATEWAY',
      message: 'The government waybill request failed and automatic retries were exhausted.',
    }),
    waybill_outcome_unknown: createError({
      errorId: 'bayan_waybill_outcome_unknown',
      statusCode: 'BAD_GATEWAY',
      message:
        'Bayan did not answer the waybill request; it may have been filed. Check the Bayan portal before filing again.',
    }),
    waybill_refile_held: createError({
      errorId: 'bayan_waybill_refile_held',
      statusCode: 'CONFLICT',
      message:
        'The last waybill request for this order got no answer from Bayan. Filing again is held until staff confirm nothing was filed.',
    }),
    waybill_credit_release_failed: createError({
      errorId: 'bayan_waybill_credit_release_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'The waybill attempt failed and the document credit could not be released.',
    }),
    waybill_good_types_degraded: createError({
      errorId: 'bayan_waybill_good_types_degraded',
      statusCode: 'BAD_GATEWAY',
      message: 'Bayan cargo types could not be loaded; a static fallback map is in use.',
    }),
    waybill_good_types_drift: createError({
      errorId: 'bayan_waybill_good_types_drift',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message:
        'A cargo type Hero maps automatically now has sub-types in Bayan; re-capture the snapshot and update the map.',
    }),
    waybill_missing_vehicle: createError({
      errorId: 'bayan_waybill_missing_vehicle',
      statusCode: 'BAD_REQUEST',
      message: 'No vehicle is assigned to this order.',
    }),
    waybill_plate_missing: createError({
      errorId: 'bayan_waybill_plate_missing',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle plate number is missing.',
    }),
    waybill_plate_unparseable: createError({
      errorId: 'bayan_waybill_plate_unparseable',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle plate could not be read.',
    }),
    waybill_plate_letters_invalid: createError({
      errorId: 'bayan_waybill_plate_letters_invalid',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle plate must have exactly three letters.',
    }),
    waybill_plate_number_invalid: createError({
      errorId: 'bayan_waybill_plate_number_invalid',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle plate must have exactly four digits.',
    }),
    waybill_missing_vehicle_plate_type: createError({
      errorId: 'bayan_waybill_missing_vehicle_plate_type',
      statusCode: 'BAD_REQUEST',
      message: 'The vehicle has no plate type (نوع اللوحة).',
    }),
    waybill_missing_driver: createError({
      errorId: 'bayan_waybill_missing_driver',
      statusCode: 'BAD_REQUEST',
      message: 'No driver is assigned to this order.',
    }),
    waybill_missing_driver_identity: createError({
      errorId: 'bayan_waybill_missing_driver_identity',
      statusCode: 'BAD_REQUEST',
      message: 'The driver has no identity number on their profile.',
    }),
    waybill_invalid_driver_identity: createError({
      errorId: 'bayan_waybill_invalid_driver_identity',
      statusCode: 'BAD_REQUEST',
      message: "The driver's identity number is not valid for Bayan.",
    }),
    waybill_missing_driver_issue_number: createError({
      errorId: 'bayan_waybill_missing_driver_issue_number',
      statusCode: 'BAD_REQUEST',
      message: 'The driver has no identity version number on their profile.',
    }),
    waybill_invalid_driver_mobile: createError({
      errorId: 'bayan_waybill_invalid_driver_mobile',
      statusCode: 'BAD_REQUEST',
      message: "The driver's phone is not a valid Saudi mobile number.",
    }),
    waybill_missing_carrier_moi: createError({
      errorId: 'bayan_waybill_missing_carrier_moi',
      statusCode: 'BAD_REQUEST',
      message:
        'The carrier has no unified establishment number (الرقم الموحد / هوية صاحب العمل, starts with 70).',
    }),
    waybill_invalid_carrier_moi: createError({
      errorId: 'bayan_waybill_invalid_carrier_moi',
      statusCode: 'BAD_REQUEST',
      message: "The carrier's unified number is not valid for Bayan — it must start with 70.",
    }),
    waybill_carrier_type_vehicle_mismatch: createError({
      errorId: 'bayan_waybill_carrier_type_vehicle_mismatch',
      statusCode: 'BAD_REQUEST',
      message:
        "Bayan rejected the waybill because the declared carrier type does not match the truck's NAQL registration.",
    }),
    // Observed 11 Sep 2026: `The carrier is neither the owner nor the actual
    // user of the entered vehicle`. The الرقم الموحد we declared as
    // `carrier.moi` is not registered on that plate in NAQL as مالك or
    // مستخدم — a government registry mismatch, not a missing Hero field.
    waybill_carrier_not_vehicle_party: createError({
      errorId: 'bayan_waybill_carrier_not_vehicle_party',
      statusCode: 'BAD_REQUEST',
      message:
        'Bayan rejected the waybill because the carrier is neither the owner nor the actual user of the vehicle on NAQL.',
    }),
    // Observed in production as `naqlNoLicenseError`: the الرقم الموحد we sent
    // is not registered as a NAQL carrier — usually the driver's employer is
    // not a transport company, and the truck owner (هوية المالك on the
    // استمارة, starts with 70) is the real carrier.
    waybill_carrier_moi_not_licensed: createError({
      errorId: 'bayan_waybill_carrier_moi_not_licensed',
      statusCode: 'BAD_REQUEST',
      message:
        "Bayan rejected the waybill because the carrier's unified number is not a licensed NAQL carrier.",
    }),
    // Observed in production as `NAQL_INTEGRATION_ERROR`: a generic ELM
    // integration failure, most often the truck's plate not matching NAQL.
    waybill_integration_error: createError({
      errorId: 'bayan_waybill_integration_error',
      statusCode: 'BAD_REQUEST',
      message:
        "Bayan could not process the waybill (NAQL integration error) — most often the truck's plate.",
    }),
    // Observed 27 Sep 2026: `TRIP_DELIVERY_DATE_ISSUE receivedDate must be
    // not more than 5 days from receivedDate` on a 7-day trip.
    waybill_trip_dates_invalid: createError({
      errorId: 'bayan_waybill_trip_dates_invalid',
      statusCode: 'BAD_REQUEST',
      message: 'Bayan rejected the trip dates — the trip can be at most 5 days.',
    }),
    waybill_missing_pickup_destination: createError({
      errorId: 'bayan_waybill_missing_pickup_destination',
      statusCode: 'BAD_REQUEST',
      message: 'The order has no pickup destination.',
    }),
    waybill_missing_dropoff_destination: createError({
      errorId: 'bayan_waybill_missing_dropoff_destination',
      statusCode: 'BAD_REQUEST',
      message: 'The order has no dropoff destination.',
    }),
    waybill_missing_pickup_address: createError({
      errorId: 'bayan_waybill_missing_pickup_address',
      statusCode: 'BAD_REQUEST',
      message: 'The pickup destination has no address.',
    }),
    waybill_missing_dropoff_address: createError({
      errorId: 'bayan_waybill_missing_dropoff_address',
      statusCode: 'BAD_REQUEST',
      message: 'The dropoff destination has no address.',
    }),
    waybill_city_unmapped: createError({
      errorId: 'bayan_waybill_city_unmapped',
      statusCode: 'BAD_REQUEST',
      message:
        'The pickup or dropoff city has no Bayan city id. Bayan needs the city name, not the neighborhood.',
    }),
    waybill_missing_sender_name: createError({
      errorId: 'bayan_waybill_missing_sender_name',
      statusCode: 'BAD_REQUEST',
      message: 'The order has no sender name.',
    }),
    waybill_missing_recipient_name: createError({
      errorId: 'bayan_waybill_missing_recipient_name',
      statusCode: 'BAD_REQUEST',
      message: 'The order has no recipient name.',
    }),
    waybill_invalid_sender_phone: createError({
      errorId: 'bayan_waybill_invalid_sender_phone',
      statusCode: 'BAD_REQUEST',
      message: 'The sender has no valid Saudi mobile number.',
    }),
    waybill_invalid_recipient_phone: createError({
      errorId: 'bayan_waybill_invalid_recipient_phone',
      statusCode: 'BAD_REQUEST',
      message: 'The recipient has no valid Saudi mobile number.',
    }),
    waybill_missing_items: createError({
      errorId: 'bayan_waybill_missing_items',
      statusCode: 'BAD_REQUEST',
      message: 'The order has no items.',
    }),
    waybill_good_type_required: createError({
      errorId: 'bayan_waybill_good_type_required',
      statusCode: 'BAD_REQUEST',
      message: 'Bayan requires an explicit cargo type for this load.',
    }),
    waybill_good_type_not_a_leaf: createError({
      errorId: 'bayan_waybill_good_type_not_a_leaf',
      statusCode: 'BAD_REQUEST',
      message: 'Bayan requires the most specific cargo type.',
    }),
    waybill_missing_item_weight: createError({
      errorId: 'bayan_waybill_missing_item_weight',
      statusCode: 'BAD_REQUEST',
      message: 'Bayan rejected the waybill over the cargo weight.',
    }),
    waybill_missing_item_price: createError({
      errorId: 'bayan_waybill_missing_item_price',
      statusCode: 'BAD_REQUEST',
      message: 'The load has no declared value.',
    }),
    waybill_missing_fare: createError({
      errorId: 'bayan_waybill_missing_fare',
      statusCode: 'BAD_REQUEST',
      message: 'Bayan rejected the waybill over the transport fare.',
    }),
    waybill_missing_dangerous_code: createError({
      errorId: 'bayan_waybill_missing_dangerous_code',
      statusCode: 'BAD_REQUEST',
      message: 'Dangerous goods require a UN code Hero does not yet capture.',
    }),
    waybill_missing_chassis_or_sequence_number: createError({
      errorId: 'bayan_waybill_missing_chassis_or_sequence_number',
      statusCode: 'BAD_REQUEST',
      message: 'Vehicle transportation requires a chassis or sequence number.',
    }),
    waybill_subscription_required: createError({
      errorId: 'bayan_waybill_subscription_required',
      statusCode: 'PAYMENT_REQUIRED',
      message: 'An active subscription is needed to issue waybills.',
    }),
    setup_owner_operator_invalid: createError({
      errorId: 'bayan_setup_owner_operator_invalid',
      statusCode: 'BAD_REQUEST',
      message:
        'Could not enable government waybills for this owner-operator — the setup card is missing or has invalid fields (identity number, identity version, plate, vehicle type, or unified number).',
    }),
    connect_missing_client_id: createError({
      errorId: 'bayan_connect_missing_client_id',
      statusCode: 'BAD_REQUEST',
      message: 'Could not connect Logisti — no Bayan client id was provided on the connect card.',
    }),
  },
  [MainRoutes.agreement]: {
    region_not_found: createError({
      errorId: 'agreement_region_not_found',
      statusCode: 'BAD_REQUEST',
      message:
        'One of the regions in the agreement is not found or has not been saved yet to the Regions database',
    }),
    org_already_has_one: createError({
      errorId: 'agreement_org_already_has_one',
      statusCode: 'BAD_REQUEST',
      message:
        'There is already an active or draft agreement between your organization and this counterparty. Terminate or expire the existing one before creating a new agreement.',
    }),
    org_not_found: createError({
      errorId: 'agreement_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The organization was not found',
    }),
  },
  [MainRoutes.route]: {
    region_not_found: createError({
      errorId: 'route_region_not_found',
      statusCode: 'BAD_REQUEST',
      message:
        'One of the regions in the destinations is not found or has not been saved yet to the Regions database',
    }),
    at_least_one_of_seller_or_buyer_required: createError({
      errorId: 'route_at_least_one_of_seller_or_buyer_required',
      statusCode: 'BAD_REQUEST',
      message:
        'At least one of seller or buyer must be set on a Route. Hero is a network, not a broker; routes without any party are not allowed.',
    }),
    self_route_not_allowed: createError({
      errorId: 'route_self_route_not_allowed',
      statusCode: 'BAD_REQUEST',
      message: 'Seller and buyer cannot be the same organization on a Route.',
    }),
    vehicle_overlap: createError({
      errorId: 'route_vehicle_overlap',
      statusCode: 'BAD_REQUEST',
      message:
        'A Route with overlapping vehicles already exists for this (seller, buyer, lane). Remove the conflicting vehicle from the existing Route before adding a new one.',
    }),
    agreement_requires_both_parties: createError({
      errorId: 'route_agreement_requires_both_parties',
      statusCode: 'BAD_REQUEST',
      message:
        'A Route bound to an Agreement (agreementId set) requires both seller and buyer to be set.',
    }),
    agreement_parties_mismatch: createError({
      errorId: 'route_agreement_parties_mismatch',
      statusCode: 'BAD_REQUEST',
      message:
        'The seller and buyer of this Route do not match the parties of the linked Agreement.',
    }),
    not_authorized_for_route: createError({
      errorId: 'route_not_authorized_for_route',
      statusCode: 'FORBIDDEN',
      message: 'Only the seller or buyer of a Route (or a super admin) may manage it.',
    }),
    already_exists: createError({
      errorId: 'route_already_exists',
      statusCode: 'BAD_REQUEST',
      message: 'The route you are trying to create already exists',
    }),
    party_not_found: createError({
      errorId: 'route_party_not_found',
      statusCode: 'BAD_REQUEST',
      message:
        'The seller or buyer org id does not match any Business or Carrier. Check that the caller is signed in as a known organization.',
    }),
  },
  invoice: {
    bulk_service_invalid_target_payment_user: createError({
      errorId: 'invoice_bulk_service_invalid_target_payment_user',
      statusCode: 'BAD_REQUEST',
      message: 'The provided beneficiary target type is wrong for the bulk service.',
    }),
    wrong_beneficiary_target: createError({
      errorId: 'invoice_wrong_beneficiary_target',
      statusCode: 'BAD_REQUEST',
      message: 'The provided beneficiary target type is wrong.',
    }),
    wrong_beneficiary_user_type: createError({
      errorId: 'invoice_wrong_beneficiary_user_type',
      statusCode: 'BAD_REQUEST',
      message: 'The provided beneficiary target does not match any user type.',
    }),
    wrong_matching_info: createError({
      errorId: 'invoice_wrong_matching_info',
      statusCode: 'BAD_REQUEST',
      message:
        'The provided info such as payer or receiver is wrong, and does not match the target invoice.',
    }),

    more_than_one_paid_invoice: createError({
      errorId: 'invoice_more_than_one_paid_invoice',
      statusCode: 'BAD_REQUEST',
      message:
        'The invoice has more than one paid invoice. When paying for a single order which is wrong, there must be only one paid invoice when paying for a single order.',
    }),
    cannot_upload_proof_in_current_status: createError({
      errorId: 'invoice_cannot_upload_proof_in_current_status',
      statusCode: 'BAD_REQUEST',
      message: 'Cannot upload invoice proof in the current invoice status.',
    }),
    not_authorized_to_upload_proof: createError({
      errorId: 'invoice_not_authorized_to_upload_proof',
      statusCode: 'FORBIDDEN',
      message: 'You are not authorized to upload proof for this invoice.',
    }),
    proof_not_found: createError({
      errorId: 'invoice_proof_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'No invoice proof exists on this invoice to review.',
    }),
  },
  [MainRoutes.financing]: {
    invoice_upload_failed: createError({
      errorId: 'financing_invoice_upload_failed',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while uploading the invoice document',
    }),
    profile_required: createError({
      errorId: 'financing_profile_required',
      statusCode: 'BAD_REQUEST',
      message: 'Save your financing profile before submitting your first application',
    }),
    no_lenders: createError({
      errorId: 'financing_no_lenders',
      statusCode: 'BAD_REQUEST',
      message:
        'No active financing partners are configured on the platform yet — please try again later or contact support',
    }),
    too_many_lenders: createError({
      errorId: 'financing_too_many_lenders',
      statusCode: 'BAD_REQUEST',
      message: 'Too many lenders targeted in a single application',
    }),
    lender_revoked: createError({
      errorId: 'financing_lender_revoked',
      statusCode: 'BAD_REQUEST',
      message: 'One of the targeted lenders is no longer active',
    }),
    lender_inactive: createError({
      errorId: 'financing_lender_inactive',
      statusCode: 'BAD_REQUEST',
      message: 'One of the targeted lenders is blocked or inactive',
    }),
    invalid_lender_link: createError({
      errorId: 'financing_invalid_lender_link',
      statusCode: 'BAD_REQUEST',
      message: 'This lender link is invalid or has expired',
    }),
    offer_not_found: createError({
      errorId: 'financing_offer_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The selected offer was not found on this application',
    }),
    offer_not_found_for_lender: createError({
      errorId: 'financing_offer_not_found_for_lender',
      statusCode: 'BAD_REQUEST',
      message: 'No offer exists for this lender on this application',
    }),
    offer_not_offered: createError({
      errorId: 'financing_offer_not_offered',
      statusCode: 'BAD_REQUEST',
      message:
        'The selected offer is not in `offered` state (the lender has not submitted terms yet)',
    }),
    offer_already_accepted_elsewhere: createError({
      errorId: 'financing_offer_already_accepted_elsewhere',
      statusCode: 'BAD_REQUEST',
      message:
        'This application has already accepted a different offer; funding cannot be routed to a new lender',
    }),
    offer_already_accepted: createError({
      errorId: 'financing_offer_already_accepted',
      statusCode: 'BAD_REQUEST',
      message:
        'This offer has already been accepted by the carrier — admins cannot overwrite an accepted offer',
    }),
    funding_requires_offer_id: createError({
      errorId: 'financing_funding_requires_offer_id',
      statusCode: 'BAD_REQUEST',
      message: 'Provide an offerId to fund (the application has not been accepted yet)',
    }),
    admin_offer_lender_required: createError({
      errorId: 'financing_admin_offer_lender_required',
      statusCode: 'BAD_REQUEST',
      message: 'A lenderId is required when appending a new offer via the admin upsert endpoint',
    }),
    admin_offer_lender_already_present: createError({
      errorId: 'financing_admin_offer_lender_already_present',
      statusCode: 'BAD_REQUEST',
      message:
        'An offer for this lender already exists on the application; use edit mode (pass offerId) instead',
    }),
    lender_not_found: createError({
      errorId: 'financing_lender_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The selected lender was not found',
    }),
    lender_not_active: createError({
      errorId: 'financing_lender_not_active',
      statusCode: 'BAD_REQUEST',
      message: 'The selected lender is not active',
    }),
    offer_lost: createError({
      errorId: 'financing_offer_lost',
      statusCode: 'BAD_REQUEST',
      message: 'This offer is no longer eligible — the carrier already accepted a different lender',
    }),
    offer_not_accepted_for_funding: createError({
      errorId: 'financing_offer_not_accepted_for_funding',
      statusCode: 'BAD_REQUEST',
      message:
        'The carrier has not accepted this offer yet — funding can only be recorded after acceptance',
    }),
    already_funded: createError({
      errorId: 'financing_already_funded',
      statusCode: 'BAD_REQUEST',
      message: 'This application has already been funded — funding cannot be recorded twice',
    }),
    not_funded_yet: createError({
      errorId: 'financing_not_funded_yet',
      statusCode: 'BAD_REQUEST',
      message:
        'The application has not been funded yet — repayment cannot be recorded before funding',
    }),
    already_repaid: createError({
      errorId: 'financing_already_repaid',
      statusCode: 'BAD_REQUEST',
      message: 'This application has already been repaid — repayment cannot be recorded twice',
    }),
    no_receipt_to_review: createError({
      errorId: 'financing_no_receipt_to_review',
      statusCode: 'BAD_REQUEST',
      message: 'No repayment receipt has been submitted yet — there is nothing to review',
    }),
    application_closed: createError({
      errorId: 'financing_application_closed',
      statusCode: 'BAD_REQUEST',
      message: 'This application is no longer accepting changes',
    }),
    forbidden: createError({
      errorId: 'financing_forbidden',
      statusCode: 'FORBIDDEN',
      message: 'You are not allowed to perform this action on this application',
    }),
    org_required: createError({
      errorId: 'financing_org_required',
      statusCode: 'BAD_REQUEST',
      message: 'An organization context is required for this action',
    }),
    org_not_found: createError({
      errorId: 'financing_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The organization was not found',
    }),
    partner_invite_not_found: createError({
      errorId: 'financing_partner_invite_not_found',
      statusCode: 'NOT_FOUND',
      message: 'This financing partner invite link is invalid or has expired',
    }),
    interest_not_expressed: createError({
      errorId: 'financing_interest_not_expressed',
      statusCode: 'BAD_REQUEST',
      message: 'Express interest before submitting an offer on this application',
    }),
    advance_exceeds_requested: createError({
      errorId: 'financing_advance_exceeds_requested',
      statusCode: 'BAD_REQUEST',
      message: 'Advance amount cannot exceed the requested amount on the application',
    }),
    application_not_pending_review: createError({
      errorId: 'financing_application_not_pending_review',
      statusCode: 'BAD_REQUEST',
      message: 'This application has already moved past pending_review and cannot be sent again',
    }),
  },
  [MainRoutes.payment]: {
    missing_rebalance_transaction: createError({
      errorId: 'payment_missing_rebalance_transaction',
      statusCode: 'BAD_REQUEST',
      message: 'The rebalance transaction is missing',
    }),

    invalid_transaction_method: createError({
      errorId: 'payment_invalid_transaction_method',
      statusCode: 'BAD_REQUEST',
      message: 'The provided transaction method is wrong to get the expected status.',
    }),
    invalid_payment_method: createError({
      errorId: 'payment_invalid_payment_method',
      statusCode: 'BAD_REQUEST',
      message: 'The provided payment method is wrong.',
    }),
    order_driver_beneficiary_not_found: createError({
      errorId: 'payment_order_driver_beneficiary_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The order does not have a driver as a beneficiary.',
    }),
    invalid_beneficiary_type: createError({
      errorId: 'payment_invalid_beneficiary_type',
      statusCode: 'BAD_REQUEST',
      message: 'The provided beneficiary type is wrong, or not allowed to make a payment with.',
    }),
    order_not_found: createError({
      errorId: 'payment_order_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The order was not found',
    }),
    hyperpay_error_create_checkout: createError({
      errorId: 'payment_hyperpay_error_create_checkout',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while creating a checkout',
    }),
    hyperpay_unsuccessful_code_checkout: createError({
      errorId: 'payment_hyperpay_unsuccessful_code_checkout',
      statusCode: 'BAD_REQUEST',
      message: 'The checkout code was unsuccessful.',
    }),
    hyperpay_error_get_payment_status: createError({
      errorId: 'payment_hyperpay_error_get_payment_status',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while getting the payment status',
    }),
    hyperpay_webhook_error: createError({
      errorId: 'payment_hyperpay_webhook_error',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong in Hyper Pay webhook.',
    }),
    bank_transfer_missing_proof_of_transaction: createError({
      errorId: 'payment_bank_transfer_missing_proof_of_transaction',
      statusCode: 'BAD_REQUEST',
      message: 'The bank transfer payment method requires a proof of transaction image uploaded',
    }),
    error_while_creating_transaction: createError({
      errorId: 'payment_error_while_creating_transaction',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while creating a transaction',
    }),
    missing_order_id: createError({
      errorId: 'payment_missing_order_id',
      statusCode: 'BAD_REQUEST',
      message: 'The order id is missing create a new payment',
    }),
    missing_invoice_id: createError({
      errorId: 'payment_missing_invoice_id',
      statusCode: 'BAD_REQUEST',
      message: 'The invoice id is missing to create a new payment',
    }),
    invoice_not_found_in_transaction: createError({
      errorId: 'payment_invoice_not_found_in_transaction',
      statusCode: 'BAD_REQUEST',
      message: 'The invoice is not found in the transaction invoices array',
    }),
    invoice_unused_amount_negative: createError({
      errorId: 'payment_invoice_unused_amount_negative',
      statusCode: 'BAD_REQUEST',
      message: 'The invoice unused amount is not allowed to be negative.',
    }),
    missing_target_id: createError({
      errorId: 'payment_missing_target_id',
      statusCode: 'BAD_REQUEST',
      message: 'The target id is missing to create a new payment',
    }),
    create_missing_info: createError({
      errorId: 'payment_create_missing_info',
      statusCode: 'BAD_REQUEST',
      message: 'Some info is missing such as beneficiary or amount',
    }),
    missing_invoice: createError({
      errorId: 'payment_missing_invoice',
      statusCode: 'BAD_REQUEST',
      message: 'The invoice is missing to link the order to a payment',
    }),
    missing_gateway_method: createError({
      errorId: 'payment_missing_gateway_method',
      statusCode: 'BAD_REQUEST',
      message:
        'The transaction type is an actual payment transaction, but the gateway method is missing',
    }),
    invoices_mixed_currencies: createError({
      errorId: 'payment_invoices_mixed_currencies',
      statusCode: 'BAD_REQUEST',
      message: 'All selected invoices must have the same currency',
    }),
  },
  /**
   * Errors raised by `OrgProfileService` — the section-keyed, partial
   * `/org/profile` API that replaced the legacy organization/profile
   * mapper. Lives under its own namespace so the legacy keys can be
   * removed cleanly when the old module is deleted.
   */
  orgProfile: {
    current_user_does_not_belong_to_org: createError({
      errorId: 'orgProfile_current_user_does_not_belong_to_org',
      statusCode: 'BAD_REQUEST',
      message: 'The current user does not belong to any organization',
    }),
    bank_only_for_carriers: createError({
      errorId: 'orgProfile_bank_only_for_carriers',
      statusCode: 'BAD_REQUEST',
      message:
        'Bank profile updates are only allowed for organizations with a carrier role. Add the carrier role first or remove `bank` from the payload.',
    }),
    bank_partial_update_requires_existing_bank: createError({
      errorId: 'orgProfile_bank_partial_update_requires_existing_bank',
      statusCode: 'BAD_REQUEST',
      message:
        'A partial bank update is only valid against an existing primary bank account. Send all four fields (bankName, IBAN, accountName, accountImage) to create one.',
    }),
  },
  transaction: {
    missing_payment_method: createError({
      errorId: 'transaction_missing_payment_method',
      message:
        'Based on the provided transaction type, there must be a payment method to create the transaction',
      statusCode: 'BAD_REQUEST',
    }),
    user_type_not_match_transaction_type: createError({
      errorId: 'transaction_user_type_not_match_transaction_type',
      message: 'The new transaction to be created does not match the target user type',
      statusCode: 'BAD_REQUEST',
    }),
  },
  onesignal: {
    notification_error: createError({
      errorId: 'onesignal_notification_error',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while sending a notification',
    }),
    create_user_error: createError({
      errorId: 'onesignal_create_user_error',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while create a new user on One Signal',
    }),
    get_user_error: createError({
      errorId: 'onesignal_get_user_error',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while fetching user info in One Signal',
    }),
    create_subscription_error: createError({
      errorId: 'onesignal_create_subscription_error',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while create a new subscription for a One Signal user',
    }),
  },

  bank: {
    missing_holder_error: createError({
      errorId: 'bank_missing_holder_error',
      statusCode: 'BAD_REQUEST',
      message: 'No Holder Info was provided',
    }),
  },
  contact: {
    org_not_found: createError({
      errorId: 'contact_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The org was not found in the current user relation',
    }),
  },
  flags: {
    sdk_key_missing: createError({
      errorId: 'flags_sdk_key_missing',
      statusCode: 'BAD_REQUEST',
      message: 'LaunchDarkly SDK key is not defined in environment variables',
    }),
    evaluation_error: createError({
      errorId: 'flags_evaluation_error',
      statusCode: 'BAD_REQUEST',
      message: 'Error evaluating feature flag',
    }),
    tracking_error: createError({
      errorId: 'flags_tracking_error',
      statusCode: 'BAD_REQUEST',
      message: 'Error tracking feature flag event',
    }),
  },
  relation: {
    org_not_found: createError({
      errorId: 'relation_org_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'No business or carrier was found in relation to the provided org id',
    }),
    user_not_found: createError({
      errorId: 'relation_user_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The user or driver by the provided id was not found',
    }),
  },
  whatsapp: {
    template_id_not_found: createError({
      errorId: 'whatsapp_template_id_not_found',
      statusCode: 'BAD_REQUEST',
      message: 'The WhatsApp template id was not found',
    }),
    failed_to_send_message: createError({
      errorId: 'whatsapp_failed_to_send_message',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while sending a WhatsApp message',
    }),
    wire_record_failed: createError({
      errorId: 'whatsapp_wire_record_failed',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message:
        'A WhatsApp message was sent but its wire record could not be written to the transcript',
    }),
    template_not_registered: createError({
      errorId: 'whatsapp_template_not_registered',
      statusCode: 'BAD_REQUEST',
      message:
        'The WhatsApp template has no provider-side registration yet (bot webhook, media handle, or Meta approval is missing)',
    }),
    signup_requires_phone_thread: createError({
      errorId: 'whatsapp_signup_requires_phone_thread',
      statusCode: 'BAD_REQUEST',
      message: 'In-chat signup needs a phone-backed thread — the conversation has no sender number',
    }),
    outside_session_window: createError({
      errorId: 'whatsapp_outside_session_window',
      statusCode: 'BAD_REQUEST',
      message:
        'The 24-hour WhatsApp session window is closed and no approved template is available for this message',
    }),
    already_queued: createError({
      errorId: 'whatsapp_already_queued',
      statusCode: 'BAD_REQUEST',
      message:
        'An identical message is already on this thread’s outbound queue — the send was deduplicated, not lost',
    }),
    cloud_not_configured: createError({
      errorId: 'whatsapp_cloud_not_configured',
      statusCode: 'BAD_REQUEST',
      message:
        'This WhatsApp ChannelAccount has no 360dialog API key — set credentialsRef to an env-var name and mount the secret',
    }),
    inbound_unauthorized: createError({
      errorId: 'whatsapp_inbound_unauthorized',
      statusCode: 'UNAUTHORIZED',
      message:
        '360dialog Cloud inbound rejected — Hub must send WHATSAPP_360DIALOG_WEBHOOK_SECRET as ?token=, Bearer, or X-WhatsApp-360dialog',
    }),
    inbound_dropped: createError({
      errorId: 'whatsapp_inbound_dropped',
      statusCode: 'BAD_REQUEST',
      message:
        'Cloud inbound was ACKed but the agent turn was dropped — no matching active 360dialog ChannelAccount for this phone_number_id',
    }),
    media_download_failed: createError({
      errorId: 'whatsapp_media_download_failed',
      statusCode: 'BAD_REQUEST',
      message: '360dialog Cloud media download failed — the attachment was not stored',
    }),
  },
  devTools: {
    dangerous_operation_not_allowed: createError({
      errorId: 'devTools_dangerous_operation_not_allowed',
      statusCode: 'FORBIDDEN',
      message: 'This operation is not allowed in production',
    }),
  },
  moyasar: {
    unsupported_currency: createError({
      errorId: 'moyasar_unsupported_currency',
      statusCode: 'BAD_REQUEST',
      message: 'The provided currency is not supported by Moyasar integration',
    }),
    invalid_amount: createError({
      errorId: 'moyasar_invalid_amount',
      statusCode: 'BAD_REQUEST',
      message: 'The amount sent to Moyasar is invalid (not finite or negative)',
    }),
    invalid_minor_amount: createError({
      errorId: 'moyasar_invalid_minor_amount',
      statusCode: 'BAD_REQUEST',
      message: 'The minor-unit amount returned by Moyasar is invalid',
    }),
    missing_credentials: createError({
      errorId: 'moyasar_missing_credentials',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Moyasar API credentials are not configured',
    }),
    request_failed: createError({
      errorId: 'moyasar_request_failed',
      statusCode: 'BAD_GATEWAY',
      message: 'Moyasar API call failed',
    }),
    webhook_invalid_signature: createError({
      errorId: 'moyasar_webhook_invalid_signature',
      statusCode: 'UNAUTHORIZED',
      message: 'Moyasar webhook signature did not validate',
    }),
    webhook_handler_missing: createError({
      errorId: 'moyasar_webhook_handler_missing',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'Moyasar webhook received but no in-process handler is registered',
    }),
  },
  events: {
    no_listener: createError({
      errorId: 'events_no_listener',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'A required domain event was emitted with no @OnEvent listener registered',
    }),
  },
  subscription: {
    not_found: createError({
      errorId: 'subscription_not_found',
      statusCode: 'NOT_FOUND',
      message: 'Subscription not found',
    }),
    already_exists: createError({
      errorId: 'subscription_already_exists',
      statusCode: 'BAD_REQUEST',
      message: 'This account already has an active subscription',
    }),
    owner_not_freelancer: createError({
      errorId: 'subscription_owner_not_freelancer',
      statusCode: 'BAD_REQUEST',
      message:
        'Only a freelancer driver can hold a subscription in their own name; a company driver is billed through their organization',
    }),
    unknown_plan: createError({
      errorId: 'subscription_unknown_plan',
      statusCode: 'BAD_REQUEST',
      message: 'The plan code is not configured in the subscription catalog',
    }),
    unknown_addon: createError({
      errorId: 'subscription_unknown_addon',
      statusCode: 'BAD_REQUEST',
      message: 'The addon code is not configured in the subscription catalog',
    }),
    price_not_configured: createError({
      errorId: 'subscription_price_not_configured',
      statusCode: 'BAD_REQUEST',
      message: 'No price configured for this plan / currency / interval combination',
    }),
    addon_price_not_configured: createError({
      errorId: 'subscription_addon_price_not_configured',
      statusCode: 'BAD_REQUEST',
      message: 'No price configured for this addon / currency / cadence combination',
    }),
    currency_mismatch: createError({
      errorId: 'subscription_currency_mismatch',
      statusCode: 'BAD_REQUEST',
      message: 'Cannot mix currencies on a single subscription',
    }),
    no_payment_method: createError({
      errorId: 'subscription_no_payment_method',
      statusCode: 'BAD_REQUEST',
      message: 'Subscription has no saved payment method; user must add a card',
    }),
    cannot_renew: createError({
      errorId: 'subscription_cannot_renew',
      statusCode: 'BAD_REQUEST',
      message: 'Subscription is not eligible for renewal (canceled or paused)',
    }),
    cannot_pay: createError({
      errorId: 'subscription_cannot_pay',
      statusCode: 'BAD_REQUEST',
      message: 'Subscription is not eligible for self-serve payment in its current state',
    }),
    cancel_failed: createError({
      errorId: 'subscription_cancel_failed',
      statusCode: 'BAD_REQUEST',
      message: 'Unable to cancel subscription in its current state',
    }),
    invalid_seat_change: createError({
      errorId: 'subscription_invalid_seat_change',
      statusCode: 'BAD_REQUEST',
      message: 'Provide a committed vehicle or order quantity to resize the commitment',
    }),
    /**
     * The plan meters order volume but the caller sent no
     * `committedOrderQty` (or the reverse). Caught before a Subscription
     * is written so nobody ends up on a plan that prices to zero.
     */
    missing_committed_qty: createError({
      errorId: 'subscription_missing_committed_qty',
      statusCode: 'BAD_REQUEST',
      message: 'The selected plan requires a committed quantity for its billing basis',
    }),
  },
  billing: {
    not_found: createError({
      errorId: 'billing_not_found',
      statusCode: 'NOT_FOUND',
      message: 'Billing document not found',
    }),
    cannot_finalize: createError({
      errorId: 'billing_cannot_finalize',
      statusCode: 'BAD_REQUEST',
      message: 'Billing cannot be finalized in its current status',
    }),
    cannot_apply_payment: createError({
      errorId: 'billing_cannot_apply_payment',
      statusCode: 'BAD_REQUEST',
      message: 'Billing cannot accept additional payment in its current status',
    }),
    cannot_refund: createError({
      errorId: 'billing_cannot_refund',
      statusCode: 'BAD_REQUEST',
      message: 'Billing cannot be refunded in its current status',
    }),
    items_required: createError({
      errorId: 'billing_items_required',
      statusCode: 'BAD_REQUEST',
      message: 'Billing requires at least one item',
    }),
    cannot_cancel: createError({
      errorId: 'billing_cannot_cancel',
      statusCode: 'BAD_REQUEST',
      message: 'Billing cannot be cancelled once paid',
    }),
    cannot_update: createError({
      errorId: 'billing_cannot_update',
      statusCode: 'BAD_REQUEST',
      message: 'Billing cannot be repriced in its current status',
    }),
    gateway_unsupported: createError({
      errorId: 'billing_gateway_unsupported',
      statusCode: 'INTERNAL_SERVER_ERROR',
      message: 'This payment gateway is not wired yet',
    }),
  },
  billingTransaction: {
    not_found: createError({
      errorId: 'billingTransaction_not_found',
      statusCode: 'NOT_FOUND',
      message: 'BillingTransaction not found',
    }),
    charge_failed: createError({
      errorId: 'billingTransaction_charge_failed',
      statusCode: 'BAD_REQUEST',
      message: 'BillingTransaction charge attempt failed at the gateway',
    }),
    already_terminal: createError({
      errorId: 'billingTransaction_already_terminal',
      statusCode: 'BAD_REQUEST',
      message: 'BillingTransaction already reached a terminal status',
    }),
    no_token_available: createError({
      errorId: 'billingTransaction_no_token_available',
      statusCode: 'BAD_REQUEST',
      message: 'No saved payment token to charge',
    }),
  },
  features: {
    blocked_by_platform: createError({
      errorId: 'features_blocked_by_platform',
      statusCode: 'FORBIDDEN',
      message: 'Your access is blocked by the platform billing status',
    }),
    org_not_found: createError({
      errorId: 'features_org_not_found',
      statusCode: 'NOT_FOUND',
      message: 'No organization context found to resolve features',
    }),
  },
  email: {
    send_failed: createError({
      errorId: 'email_send_failed',
      statusCode: 'BAD_REQUEST',
      message: 'Something went wrong while sending the email',
    }),
    rate_limited: createError({
      errorId: 'email_rate_limited',
      statusCode: 'TOO_MANY_REQUESTS',
      message: 'Email provider rate limit exceeded — please retry shortly or contact support',
    }),
  },
} as const;

/** English catalog message for an `errorId` such as `bayan_waybill_missing_fare`. */
export const getApiErrorMessage = (errorId: string): string | undefined => {
  const components = split(errorId, '_');
  if (components.length < 2) return undefined;
  const module = components[0];
  if (!(module in API_ERRORS)) return undefined;
  const messageId = join(components.slice(1), '_');
  // @ts-expect-error — dynamic API_ERRORS module/messageId indexing
  return (API_ERRORS[module][messageId] as APIErrorInfo | undefined)?.message;
};

type Obj = typeof API_ERRORS;
type FlattenedAPIKeys = {
  [K in APIErrorModules as `${K & string}_${keyof Obj[K] & string}`]: Obj[K][keyof Obj[K]];
};

export type APIErrorModules = keyof Obj;
export type APIErrorIds = keyof FlattenedAPIKeys;
export type APIErrorRes = {
  module: APIErrorModules;
  errorId: APIErrorIds;
  message?: string;
};
